import styled from 'styled-components';

interface FilterProps {
    isOpen: boolean;
  }

export const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

export const PageTitle = styled.h1`
  margin: 0 0 2rem 0;
  font-size: 2.5rem;
  font-weight: bold;
`;

export const StatsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
    position: relative;
    padding-top: 2rem;
  }
`;

export const StatsHeader = styled.div<{ variant: 'skater' | 'goalie' }>`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: ${(props) =>
      props.variant === 'goalie' ? '2fr repeat(7, 1fr)' : '2fr repeat(6, 1fr)'};
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid #f0f0f0;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--gray);
    text-align: center;

    .header-cell {
      &:first-child {
        text-align: left;
      }
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: ${(props) =>
      props.variant === 'goalie' ? '120px repeat(7, 1fr)' : '120px repeat(6, 1fr)'};
    padding: 0.5rem;
    font-size: 0.65rem;
  }
`;

export const PlayerCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 300px repeat(6, 1fr);
  align-items: center;
  gap: 1rem;
  
  .player-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0; // Enable text truncation
    
    img {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }
    
    .player-info {
      min-width: 0; // Enable text truncation
      
      h3 {
        color: var(--primary);
        margin: 0;
        font-size: 1rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .player-number {
          color: var(--gray);
          font-weight: normal;
          margin-left: 0.25rem;
        }
      }
      
      .position {
        color: var(--gray);
        font-size: 0.875rem;
      }
    }
  }
  
  .stat {
    text-align: center;
    padding: 0.5rem;
    background-color: #f5f5f5;
    border-radius: 4px;
    
    .value {
      font-size: 1.25rem;
      font-weight: bold;
      color: var(--primary);
      margin-bottom: 0.25rem;
    }
    
    .label {
      font-size: 0.75rem;
      color: var(--gray);
    }
  }

  @media (max-width: 1024px) {
    grid-template-columns: 250px repeat(6, 1fr);
    font-size: 0.9rem;
    
    .stat {
      .value {
        font-size: 1rem;
      }
      
      .label {
        font-size: 0.7rem;
      }
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 2fr repeat(6, 1fr);
    gap: 0.5rem;
    padding: 0.75rem;
    font-size: 0.8rem;

    .player-header {
      img {
        display: none;
      }

      .player-info {
        width: 100%;
        
        h3 {
          font-size: 0.9rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        
        .position {
          font-size: 0.7rem;
        }
      }
    }

    .stat {
      padding: 0.25rem;
      
      .value {
        font-size: 0.9rem;
        margin-bottom: 0;
      }
      
      .label {
        display: none;
      }
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 120px repeat(6, 1fr);
    font-size: 0.75rem;
    padding: 0.5rem;
    gap: 0.25rem;

    .player-header {
      .player-info {
        h3 {
          font-size: 0.8rem;
        }
      }
    }

    .stat {
      .value {
        font-size: 0.8rem;
      }
    }
  }
`;

export const ControlsSection = styled.div<FilterProps>`
  @media (max-width: 768px) {
    position: relative;
    margin-bottom: ${(props: FilterProps) => props.isOpen ? '1rem' : '0'};
  }
`;

export const ControlsToggle = styled.button<FilterProps>`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.75rem;
    background: white;
    border: 2px solid var(--primary);
    border-radius: 4px;
    color: var(--primary);
    font-weight: 600;
    cursor: pointer;
    margin: 1rem 0;
    gap: 0.5rem;

    &:hover {
      background: rgba(var(--primary-rgb), 0.1);
    }

    svg {
      width: 20px;
      height: 20px;
      transition: transform 0.3s ease;
      transform: ${(props: FilterProps) => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    }
  }
`;

export const ControlsPanel = styled.div<FilterProps>`
  @media (max-width: 768px) {
    display: ${(props: FilterProps) => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    left: 0;
    right: 0;
    z-index: 20;
    background: white;
    border: 2px solid var(--primary);
    border-radius: 4px;
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

export const SearchSection = styled.div`
  margin: 2rem 0;
  display: flex;
  justify-content: center;
  
  input {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    border: 2px solid var(--gray);
    border-radius: 4px;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: var(--primary);
    }
  }

  @media (max-width: 768px) {
    margin: 0;
    
    input {
      max-width: 100%;
    }
  }
`;

export const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--gray);
`;

export const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--gray);
  background: white;
  border-radius: 8px;
  margin-top: 1rem;
`;

export const SortControls = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 1rem auto;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  justify-content: center;
  max-width: fit-content;

  @media (max-width: 768px) {
    gap: 0.5rem;
    width: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;
  }
`;

export const SortButton = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background: ${props => props.isActive ? 'var(--primary)' : '#f5f5f5'};
  color: ${props => props.isActive ? 'white' : 'var(--gray)'};
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-width: 100px;
  justify-content: center;

  &:hover {
    background: ${props => props.isActive ? 'var(--primary)' : '#e5e5e5'};
  }

  @media (max-width: 768px) {
    min-width: unset;
    flex: 1;
  }
`;

export const FilterControls = styled.div<FilterProps>`
  display: flex;
  gap: 1rem;
  margin: 1rem auto;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  justify-content: center;
  max-width: fit-content;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;
  }
`;

export const FilterButton = styled.button<{ isActive: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background: ${props => props.isActive ? 'var(--primary)' : 'white'};
  color: ${props => props.isActive ? 'white' : 'var(--primary)'};
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover {
    background: ${props => props.isActive ? 'var(--primary)' : 'rgba(var(--primary-rgb), 0.1)'};
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    min-width: unset;
  }
`;

export const PlayerName = styled.h3`
  color: var(--primary);
  margin: 0;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }

  .player-number {
    color: var(--gray);
    font-weight: normal;
    margin-left: 0.25rem;
  }
`;